import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { map, catchError, tap, shareReplay } from "rxjs/operators";
import { HttpInterClientService } from "./http-inter-client.service";
import { HttpClientService } from "./http-client.service";
import { AssurantEnvelope } from "../interfaces/assurant-envelope.interface";
import {
  AssurantQuoteRequest,
  AssurantQuoteRawData,
  AssurantGapData,
  AssurantGapLike,
  AssurantSraPriceOption,
  normalizeAssurantGapData,
} from "../interfaces/assurant-quote.interface";
import { AssurantEmissionRequest } from "../interfaces/assurant-emission.interface";
import { AssurantDownloadRequest } from "../interfaces/assurant-download.interface";

@Injectable({
  providedIn: "root",
})
export class AssurantService {
  private readonly basePath = "/api/assurant";

  /**
   * In-memory cache of GAP quotes keyed by the request payload. Since the
   * service is provided in root (app-wide singleton), the quote persists across
   * form re-opens so the external Assurant API is hit only once per vehicle.
   * Failed/empty quotes are evicted so callers can retry later.
   */
  private gapQuoteCache = new Map<string, Observable<AssurantGapData | null>>();

  constructor(private http: HttpInterClientService, private apiHttp: HttpClientService) {}

  /**
   * Fetches the active SRA price catalog from the backend so the
   * quoter can populate its `sra_options` from the DB instead of hardcoded values.
   * Returns an empty array on error so callers can fall back to defaults.
   */
  public getSraPrices(): Observable<AssurantSraPriceOption[]> {
    return this.apiHttp.get<{ sra_options: AssurantSraPriceOption[] }>("/api/assurant_sra_prices").pipe(
      map((res) => res?.sra_options ?? []),
      tap(() => console.log("AssurantService.getSraPrices: Ok.")),
      catchError((err) => {
        console.error("AssurantService.getSraPrices error:", err);
        return of([]);
      })
    );
  }

  /**
   * Requests a GAP insurance quote from Assurant.
   * The broker returns Spanish keys (dictated by the external Assurant API);
   * this maps them to the app's internal English shape (AssurantGapData) so
   * everything downstream (quote, application, contract) stays in English.
   * Returns null if the request fails or has no data.
   */
  public quoteGap(payload: AssurantQuoteRequest): Observable<AssurantGapData | null> {
    const cacheKey = this.buildGapCacheKey(payload);
    const cached = this.gapQuoteCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http.post(`${this.basePath}/quote`, payload).pipe(
      map((res: AssurantEnvelope<AssurantQuoteRawData>) => this.normalizeGapData(res?.data ?? null)),
      tap((data) => {
        console.log("AssurantService.quoteGap: Ok.");
        // Don't keep an empty quote cached so callers can retry later.
        if (!data) {
          this.gapQuoteCache.delete(cacheKey);
        }
      }),
      catchError((err) => {
        console.error("AssurantService.quoteGap error:", err);
        // Evict on error so a later open re-requests instead of replaying null.
        this.gapQuoteCache.delete(cacheKey);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.gapQuoteCache.set(cacheKey, request$);
    return request$;
  }

  /** Builds a stable cache key from the fields that identify a GAP quote. */
  private buildGapCacheKey(p: AssurantQuoteRequest): string {
    return [
      p.marca,
      p.modelo,
      p.vin,
      p.canal_venta,
      p.categoria_uso,
      p.plazo_seguro,
      p.valor_factura,
      p.rfc ?? "",
      p.certificado ?? "",
    ].join("|");
  }

  /**
   * Normalizes broker Spanish / legacy persisted GAP payloads to AssurantGapData.
   * Single boundary for quote responses and for GAP loaded from applications,
   * approved plans, or saved quotes that may still use Spanish keys.
   */
  public normalizeGapData(raw: AssurantGapLike | null | undefined): AssurantGapData | null {
    return normalizeAssurantGapData(raw);
  }

  /**
   * Emits an SRA or GAP certificate.
   * The `comando` field in the payload determines the product type:
   *   - "SRA" for Seguro de Robo de Auto Partes
   *   - "MGAP" for Seguro GAP
   */
  public emission(payload: AssurantEmissionRequest): Observable<null> {
    return this.http.post(`${this.basePath}/emission`, payload).pipe(
      map((res: AssurantEnvelope<null>) => {
        if (res && res.status && res.status !== "OK") {
          throw res;
        }
        return res?.data ?? null;
      }),
      tap(() => console.log("AssurantService.emission: Ok.")),
      catchError((err) => {
        console.error("AssurantService.emission error:", err);
        // RxJS 6: throwError takes the error value directly. Passing a factory
        // (() => err) would re-emit the function itself as the error, so the
        // caller would see "() => err" instead of the real Assurant payload.
        return throwError(err);
      })
    );
  }

  /**
   * Downloads the certificate PDF for an issued SRA or GAP policy.
   * Returns the raw PDF as a Blob for browser download or display.
   */
  public downloadCertificate(payload: AssurantDownloadRequest): Observable<Blob> {
    return (
      this.http.post(
        `${this.basePath}/download`,
        payload,
        // Angular requires this cast to request a binary response via a typed HttpClient wrapper
        { responseType: "blob" as unknown as "json" }
      ) as Observable<Blob>
    ).pipe(tap(() => console.log("AssurantService.downloadCertificate: Ok.")));
  }
}
